// Shared job-progress panel: renders a job's status, per-site rows and the
// proof each kind produces — a verification grid for updates, the purge detail
// for cache.purge. Extracted from UpdatesHub so the per-site Updates tab, the
// fleet hub and the cache actions all render a job identically.

import { jobProgressLabel, isJobTerminal } from './aggregate';
import type { Job, JobSiteResult } from '../../lib/types';

const ID = 'em-mgr--job';

export interface JobProgressProps {
  job: Job;
  /** data-id prefix, so a host screen keeps its own selectors. */
  idBase?: string;
}

export function JobProgress({ job, idBase = ID }: JobProgressProps) {
  const terminal = isJobTerminal(job.status);
  const failed = job.status === 'failed' || job.status === 'partial';
  return (
    <div
      data-id={`${idBase}--job`}
      className={`rounded-lg border p-3 text-sm ${
        !terminal
          ? 'border-blue-300 dark:border-blue-800'
          : failed
            ? 'border-red-300 dark:border-red-800'
            : 'border-green-300 dark:border-green-800'
      }`}
    >
      <div className="font-medium">
        Job {job.jobId}: {jobProgressLabel(job.status)} ({job.progress}%)
      </div>
      {job.results.map((r) => (
        <JobSiteRow key={r.siteId} result={r} idBase={idBase} />
      ))}
    </div>
  );
}

function JobSiteRow({ result, idBase }: { result: JobSiteResult; idBase: string }) {
  return (
    <div className="mt-2" data-id={`${idBase}--site`}>
      <div className="text-slate-500">{result.siteId}</div>

      {result.verifications.map((v) => (
        <div
          key={v.slug}
          data-id={`${idBase}--verification`}
          className={
            v.match
              ? 'text-green-700 dark:text-green-400'
              : 'text-red-700 dark:text-red-400'
          }
        >
          {v.slug}: {v.before} → {v.now} (expected {v.expected}) {v.match ? '✓' : '✗'}
        </div>
      ))}

      {/* A purge returns no grid — the proof is which purge actually fired. */}
      {result.cache && (
        <div
          data-id={`${idBase}--cache`}
          className={
            result.cache.hostPurge
              ? 'text-green-700 dark:text-green-400'
              : 'text-slate-600 dark:text-slate-400'
          }
        >
          object cache {result.cache.objectCache ? 'flushed' : 'not flushed'}
          {result.cache.hostType && ` · host ${result.cache.hostType}`}
          {' · '}
          {result.cache.hostPurge
            ? `host purge via ${result.cache.hostMethod} ✓`
            : 'host purge did not run'}
        </div>
      )}

      {/* The per-site error is em-manager's own message (403 disabled, SG Optimizer absent, etc.) — never swallow it into a bare red status. */}
      {result.error && (
        <div
          data-id={`${idBase}--error`}
          className="text-red-700 dark:text-red-400"
        >
          {result.error}
        </div>
      )}
    </div>
  );
}
